import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type BillingPeriod,
  type LinkedCardResponse,
  subscriptionApi,
  type SubscriptionQuoteResponse
} from "@shared/api/subscription.api";
import { useAuth } from "@shared/contexts/auth-context";
import { extractErrorDetail } from "@shared/helper_lib/api-error";
import { useToast } from "@shared/helper_lib/hooks/use-toast";
import { sendMetrikaGoal } from "@shared/helper_lib/metrika";
import { clearPendingPreviewUpgrade, consumePendingPreviewUpgrade } from "@shared/helper_lib/preview-upgrade-return";
import { useBalance } from "@shared/hooks/useBalance";

import { useSeamlessCharge } from "./useSeamlessCharge";

export type CheckoutPlan = "trial" | "basic" | "pro";
type SubscriptionPlan = Exclude<CheckoutPlan, "trial">;

interface StartCheckoutOptions {
  /** Оплата в текущей вкладке: ЮKassa вернёт пользователя по return_url сюда же */
  sameTab?: boolean;
  /** Период оплаты для basic/pro. Для trial игнорируется — пробник всегда месячный */
  period?: BillingPeriod;
  /**
   * Вызывается вплотную к уходу на платёжку — когда платёж уже создан.
   * Раньше здесь может не быть ни авторизации, ни email, и до оплаты дело
   * не дойдёт: побочные эффекты вроде маркера возврата ставить рано.
   */
  onBeforeRedirect?: () => void;
}

interface EmailModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
}

export type QuoteModalState =
  | {
      kind: "upgrade";
      plan: SubscriptionPlan;
      options: StartCheckoutOptions;
      quote: SubscriptionQuoteResponse;
      /** Сохранённый способ оплаты: с ним списываем на месте, без ЮKassa */
      card?: LinkedCardResponse | null;
    }
  | {
      kind: "downgrade";
      plan: SubscriptionPlan;
      period: BillingPeriod;
      quote: SubscriptionQuoteResponse;
    };

export interface SubscriptionQuoteModalProps {
  state: QuoteModalState | null;
  isProcessing: boolean;
  onConfirm: () => void;
  onClose: () => void;
  /** Текст отказа банка при бесшовном списании */
  failureMessage?: string | null;
  /** Уйти на ЮKassa после отказа. Нет — когда деньги уже списаны */
  onPayAnotherWay?: () => void;
}

interface UseSubscriptionCheckoutParams {
  onError?: (message: string) => void;
}

interface UseSubscriptionCheckoutReturn {
  /** true — платёж создан и браузер уходит на платёжку */
  startCheckout: (plan: CheckoutPlan, options?: StartCheckoutOptions) => Promise<boolean>;
  loadingPlan: CheckoutPlan | null;
  emailModalProps: EmailModalProps;
  quoteModalProps: SubscriptionQuoteModalProps;
}

/**
 * Общий чекаут подписок: авторизация, дозапрос email и создание платежа.
 * Используется и на странице тарифов, и в CTA бесплатного превью, чтобы
 * логика оплаты не расходилась между ними.
 */
export const useSubscriptionCheckout = ({
  onError
}: UseSubscriptionCheckoutParams = {}): UseSubscriptionCheckoutReturn => {
  const { isAuthenticated, openAuthModal, needsEmailForPayment, updateUserEmail } = useAuth();
  const { balance, refetch: refetchBalance } = useBalance();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loadingPlan, setLoadingPlan] = useState<CheckoutPlan | null>(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [pending, setPending] = useState<{ plan: CheckoutPlan; options: StartCheckoutOptions } | null>(null);
  const [quoteState, setQuoteState] = useState<QuoteModalState | null>(null);
  const [isProcessingQuote, setIsProcessingQuote] = useState(false);
  const quoteInFlightRef = useRef(false);
  const { charge, isCharging, failure, reset: resetFailure } = useSeamlessCharge();

  const reportError = (message: string): void => {
    if (onError) {
      onError(message);
      return;
    }
    alert(message);
  };

  const finalizePayment = async (plan: CheckoutPlan, options: StartCheckoutOptions): Promise<boolean> => {
    setLoadingPlan(plan);
    let redirected = false;

    try {
      const result =
        plan === "trial"
          ? await subscriptionApi.createTrialPayment()
          : await subscriptionApi.createSubscriptionPayment(plan, options.period ?? "month");

      if (!result.paymentUrl) {
        console.error("[PAYMENT] No paymentUrl in response:", result);
        reportError("Ошибка: не получена ссылка для оплаты");
        return false;
      }

      // Между кликом «полная версия» и оплатой была слепая зона: 12 кликов на 7 оплат
      sendMetrikaGoal("checkout_opened", { checkout_plan: plan });

      options.onBeforeRedirect?.();

      if (options.sameTab) {
        // Переход не блокирует выполнение: кнопку не разблокируем, иначе на
        // этом окне успевает пройти второй клик
        redirected = true;
        window.location.href = result.paymentUrl;
        return true;
      }

      window.open(result.paymentUrl, "_blank");
      return true;
    } catch (error) {
      console.error("Ошибка создания платежа:", error);
      const message = extractErrorDetail(error, "Не удалось создать платёж. Попробуйте позже.");
      reportError(message);
      return false;
    } finally {
      if (!redirected) {
        setLoadingPlan(null);
      }
    }
  };

  const createPayment = async (plan: CheckoutPlan, options: StartCheckoutOptions): Promise<boolean> => {
    if (plan === "trial") {
      return finalizePayment(plan, options);
    }

    const period = options.period ?? "month";
    setLoadingPlan(plan);

    try {
      const quote = await subscriptionApi.getSubscriptionQuote(plan, period);

      if (quote.action === "same") {
        toast({ title: "Этот тариф уже подключён" });
        return false;
      }

      if (quote.action === "downgrade") {
        setQuoteState({ kind: "downgrade", plan, period, quote });
        return false;
      }

      // Карта есть — подтверждение показываем всегда, даже без зачёта: с него
      // начинается бесшовное списание. Без карты подтверждение нужно только
      // ради суммы зачёта, иначе сразу уходим на ЮKassa.
      //
      // Пробный период сюда не заходит: переход в Базовый в один клик — это
      // релиз 1b, его не катим без напоминания тем, кто не пользуется
      // сервисом. Бэкенд такой платёж тоже отклоняет.
      const card =
        balance?.plan === "trial"
          ? ({ hasCard: false } as LinkedCardResponse)
          : await subscriptionApi.getLinkedCard().catch(() => ({ hasCard: false }) as LinkedCardResponse);

      if (card.hasCard || quote.credit > 0) {
        setQuoteState({ kind: "upgrade", plan, options, quote, card });
        return false;
      }

      return finalizePayment(plan, options);
    } catch (error) {
      console.error("Ошибка расчёта смены тарифа:", error);
      const message = extractErrorDetail(error, "Не удалось рассчитать стоимость смены тарифа. Попробуйте позже.");
      reportError(message);
      return false;
    } finally {
      setLoadingPlan(null);
    }
  };

  const startCheckout = async (plan: CheckoutPlan, options: StartCheckoutOptions = {}): Promise<boolean> => {
    if (!isAuthenticated) {
      openAuthModal("signup");
      return false;
    }

    if (needsEmailForPayment) {
      setPending({ plan, options });
      setShowEmailModal(true);
      return false;
    }

    return createPayment(plan, options);
  };

  const handleEmailSuccess = async (email: string): Promise<void> => {
    updateUserEmail(email);
    setShowEmailModal(false);

    if (pending) {
      const { plan, options } = pending;
      setPending(null);
      await createPayment(plan, options);
    }
  };

  const confirmQuote = async (): Promise<void> => {
    if (!quoteState) {
      return;
    }

    if (quoteState.kind === "upgrade") {
      // Состояние кнопки обновится только после рендера: второй клик в том же
      // такте успел бы запустить второе списание
      if (quoteInFlightRef.current) {
        return;
      }
      quoteInFlightRef.current = true;

      const { plan, options, quote } = quoteState;
      const period = options.period ?? "month";
      setIsProcessingQuote(true);
      try {
        const freshQuote = await subscriptionApi.getSubscriptionQuote(plan, period);

        if (freshQuote.action !== "upgrade") {
          setQuoteState(null);
          toast({
            title:
              freshQuote.action === "same"
                ? "Этот тариф уже подключён"
                : "Переход стал понижением — выберите тариф заново"
          });
          return;
        }

        if (freshQuote.amount !== quote.amount) {
          setQuoteState({ kind: "upgrade", plan, options, quote: freshQuote, card: quoteState.card });
          toast({ title: "Сумма пересчитана — проверьте и подтвердите ещё раз" });
          return;
        }

        sendMetrikaGoal("checkout_opened", { checkout_plan: plan });

        if (quoteState.card?.hasCard) {
          // Тот же момент, что и у редиректа: платёж вот-вот уйдёт в банк.
          // Из превью здесь ставится маркер возврата к записи — без него
          // человек оплачивает и остаётся смотреть на модалку.
          options.onBeforeRedirect?.();

          const result = await charge({ kind: "subscription", plan, billingPeriod: period });

          // Отказ остаётся в модалке: там кнопка «Оплатить другой картой».
          // На 3-D Secure браузер уже уходит на страницу банка.
          if (result?.status !== "succeeded") {
            // Маркер снимаем: к записи не возвращаемся, оплаты не было
            clearPendingPreviewUpgrade();
            return;
          }

          await refetchBalance();
          setQuoteState(null);

          // Оплата пришла из превью — возвращаем человека к его записи,
          // ровно как это делает страница успеха после редиректа
          const previewId = consumePendingPreviewUpgrade();
          if (previewId) {
            navigate(`/history?transcription=${previewId}`, { state: { paymentSuccess: true } });
            return;
          }

          toast({
            title: "Тариф подключён",
            description: `${plan === "pro" ? "Профи" : "Базовый"} активен, минуты уже на балансе.`
          });
          return;
        }

        await finalizePayment(plan, options);
        setQuoteState(null);
      } catch (error) {
        console.error("Ошибка повторного расчёта смены тарифа:", error);
        const message = extractErrorDetail(error, "Не удалось подтвердить оплату. Попробуйте позже.");
        reportError(message);
      } finally {
        quoteInFlightRef.current = false;
        setIsProcessingQuote(false);
      }
      return;
    }

    const { plan, period } = quoteState;
    setIsProcessingQuote(true);
    try {
      const result = await subscriptionApi.scheduleSubscriptionChange(plan, period);
      toast({
        title: "Переход запланирован",
        description: `С ${new Date(result.effectiveDate).toLocaleDateString("ru-RU")} тариф сменится автоматически.`
      });
      await refetchBalance();
      setQuoteState(null);
    } catch (error) {
      console.error("Ошибка планирования смены тарифа:", error);
      const message = extractErrorDetail(error, "Не удалось запланировать смену тарифа. Попробуйте позже.");
      setQuoteState(null);
      if (message.startsWith("Автопродление отключено")) {
        toast({ title: "Автопродление отключено", description: message });
        return;
      }
      reportError(message);
    } finally {
      setIsProcessingQuote(false);
    }
  };

  return {
    startCheckout,
    loadingPlan,
    emailModalProps: {
      open: showEmailModal,
      onClose: (): void => {
        setShowEmailModal(false);
        setPending(null);
      },
      onSuccess: handleEmailSuccess
    },
    quoteModalProps: {
      state: quoteState,
      isProcessing: isProcessingQuote || isCharging,
      onConfirm: confirmQuote,
      onClose: (): void => {
        resetFailure();
        setQuoteState(null);
      },
      failureMessage: failure?.message ?? null,
      onPayAnotherWay: !failure?.canRetry
        ? undefined
        : (): void => {
            if (quoteState?.kind !== "upgrade") {
              return;
            }
            const { plan, options } = quoteState;
            resetFailure();
            setQuoteState(null);
            void finalizePayment(plan, options);
          }
    }
  };
};
